import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiMail, FiSend, FiShield, FiZap, FiCheck } = FiIcons;

// Email is base64-decoded at runtime (atob), so the literal "support@manuscriptsmith.com"
// never appears in the shipped JS/HTML — spam harvesters that scrape static text can't grab it.
// (Plain template concatenation gets constant-folded back by the minifier; atob does not.)
const email = () => `${atob('c3VwcG9ydA==')}@${atob('bWFudXNjcmlwdHNtaXRoLmNvbQ==')}`;

const COLUMNS = [
  {
    title: 'Product',
    links: [
      { name: 'Writing Studio', path: '/writing-studio' },
      { name: 'Cover Studio', path: '/cover-studio' },
      { name: 'Book Previewer', path: '/book-previewer' },
      { name: 'Your Author AI', path: '/author-ai' },
      { name: 'Voice DNA', path: '/voice-dna' },
      { name: 'Author Persona', path: '/author-persona' },
      { name: 'Sample analysis', path: '/analysis' },
      { name: 'Pricing', path: '/pricing' },
    ],
  },
  {
    title: 'Audiobook',
    links: [
      { name: 'Audiobook Studio', path: '/audiobook' },
      { name: 'ACX-ready mastering', path: '/audiobook/acx' },
      { name: 'Narration voices', path: '/audiobook/voices' },
    ],
  },
  {
    title: 'Learn',
    links: [
      { name: 'How it works', path: '/how-it-works' },
      { name: 'How we read', path: '/how-we-read-your-book' },
      { name: 'Authenticity', path: '/authenticity' },
      { name: 'Compare', path: '/compare' },
      { name: 'Try the demo', path: '/try' },
      { name: 'API Documentation', path: '/api-docs' },
      { name: 'Documentation', path: '/docs/', external: true },
    ],
  },
  {
    title: 'Company',
    links: [
      { name: 'For authors', path: '/for-authors' },
      { name: 'About', path: '/about' },
      { name: 'Log in', path: 'https://app.manuscriptsmith.com/login', external: true },
      { name: 'Get started', path: 'https://app.manuscriptsmith.com/signup', external: true },
    ],
  },
];

const TRUST = [
  { icon: FiShield, text: 'Your writing stays private' },
  { icon: FiCheck, text: 'KDP-ready export' },
  { icon: FiZap, text: 'Plans from $19/mo' },
];

const LEGAL = [
  { name: 'Privacy', path: '/privacy' },
  { name: 'Terms', path: '/terms' },
  { name: 'Cookies', path: '/cookies' },
];

const Footer = () => {
  const [form, setForm] = useState({ email: '', message: '', website: '' }); // website = honeypot
  const [sent, setSent] = useState(false);
  const top = () => window.scrollTo(0, 0);

  const submit = (e) => {
    e.preventDefault();
    if (form.website) return;            // honeypot tripped → silently drop (bot)
    if (form.email && form.message) setSent(true); // TODO: POST to backend w/ Turnstile when wired
  };

  return (
    <footer id="contact" className="bg-gray-900 text-white pt-12 pb-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid gap-8 lg:grid-cols-12">
          {/* Brand + trust */}
          <div className="lg:col-span-4">
            <Link to="/" onClick={top} className="flex items-center gap-2 mb-3">
              <span className="w-9 h-9 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-lg flex items-center justify-center text-white font-bold text-sm">MS</span>
              <span className="text-xl font-bold"><span className="text-primary-400">Manuscript</span><span className="text-secondary-400">Smith</span></span>
            </Link>
            <p className="text-gray-400 text-sm leading-relaxed mb-4 max-w-xs">
              Write in your own voice and ship KDP-ready manuscripts — without juggling tools.
            </p>
          </div>

          {/* Link columns */}
          <div className="lg:col-span-8 grid grid-cols-2 sm:grid-cols-4 gap-6">
            {COLUMNS.map((col) => (
              <div key={col.title}>
                <h4 className="text-white font-semibold text-sm mb-3">{col.title}</h4>
                <ul className="space-y-2">
                  {col.links.map((l) => (
                    <li key={l.name}>
                      {l.external
                        ? <a href={l.path} className="text-gray-400 hover:text-primary-400 transition-colors text-sm">{l.name}</a>
                        : <Link to={l.path} onClick={top} className="text-gray-400 hover:text-primary-400 transition-colors text-sm">{l.name}</Link>}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

        </div>

        {/* Bottom bar */}
        <div className="mt-8 pt-6 border-t border-gray-800 flex flex-col sm:flex-row justify-between items-center gap-3 text-sm text-gray-500">
          <div>© 2026 ManuscriptSmith. All rights reserved.</div>
          <div className="flex flex-wrap justify-center gap-5">
            {LEGAL.map((l) => (
              <Link key={l.name} to={l.path} onClick={top} className="hover:text-primary-400 transition-colors">{l.name}</Link>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
